import { Paginated, Query } from "@feathersjs/feathers";
import { EventInput } from "@fullcalendar/react";
import { add, endOfMonth, isAfter, startOfMonth, addMinutes } from "date-fns";
import { makeObservable, computed, reaction, runInAction, observable, action, when } from "mobx";
import client from "src/feathers";
import { Meeting, User } from "src/models";
import { RootStore, ItemStore } from ".";
import { DataType, IDeliverable, ILog, IMeeting, IQuestion, ItemType, QuestionPosition, Services, UserType } from "../types";
import { DataContainer } from "./dataContainer";
import { paramsForServer } from "feathers-hooks-common";
import toast from 'react-hot-toast';
import i18next from 'src/i18n';
import { intersect, resetTime } from "src/util/util";
import { theme } from "src/config";

export class MeetingStore extends ItemStore<Meeting, IMeeting, MeetingStore.ContainerType> {
  protected log_service = client.service(Services.Log);
  activeMeeting:  Meeting = null;
  affected_items: {total: number, processed: number} = { total: 0, processed: 0 };
  attendees:      {[user_id: number]: {in?: Date, out?: Date, checked_in: boolean}} = null;

  constructor(readonly rootStore: RootStore) {
    super(rootStore, Meeting.create, DataType.Meeting, []);

    makeObservable(this, {
      getActiveMeeting:         computed,
      getCalendarEvents:        computed,
      getAffectedItems:         computed,
      getProcessedItems:        computed,
      getActiveMeetingProgress: computed,

      activeMeeting:  observable,
      affected_items: observable,
      attendees:      observable,

      setActiveMeeting: action,
      startMeeting: action,
      stopMeeting: action,
    });

    reaction(() => this.getActiveMeeting, this.countAffectedItems);
    reaction(() => this.getActive, this.load_attendanceData);
  }

  /*******************************************************************************************************************************************
   * DB Access
   ********************************************************************************************************************************************/
  protected setupStorage() {
    super.setupStorage();
    
    // Hold next 5 user milestones in the future
    let t = MeetingStore.ContainerType.Next;
    this.storage[t] = 
      new DataContainer<IMeeting, Meeting>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    true,
            pageSize:    5,
            limited:     true,
            eventFilter: (d) => isAfter(d.date, new Date()),
            conversion:  Meeting.create,
            name: this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
  }

  protected loadActiveMeeting = async () => {
    if(this.rootStore.spaceStore.getCurRoom?.active_meeting_id == null) {
      runInAction(() => this.activeMeeting = null);
      return;
    }

    try {
      const activeMeeting = Meeting.create(await this.service.get(this.rootStore.spaceStore.getCurRoom.active_meeting_id));
      runInAction(() => this.activeMeeting = activeMeeting);
    } catch (err) {
      console.error("Failed to load active meeting", err);
    }
  }

  /**
   * Counts all affected questions and deliverables at the start
   * of a meeting. 
   */
  countAffectedItems = async (): Promise<void> => {
    if(!this.getActiveMeeting) return;
    // Get all affected questions
    try {
      const qData: Paginated<IQuestion> = await client.service(Services.Question).find({query: {
        position: {$in: [QuestionPosition.Board, QuestionPosition.Before]},
        room_id:  this.getActiveMeeting.room_id,
        decided:  false,
        $limit:   0,
      }});
      const dData: Paginated<IDeliverable> = await client.service(Services.Deliverable).find({query: {
        done:    false,
        room_id: this.getActiveMeeting.room_id,
        date:    {$lte: new Date().toISOString()},
        $limit:  0,
      }});
      const qDataP: Paginated<IQuestion> = await client.service(Services.Question).find({query: {
        position:  {$in: [QuestionPosition.Board, QuestionPosition.Before]},
        room_id:   this.getActiveMeeting.room_id,
        decided:   false,
        processed: true,
        $limit:    0,
      }});
      const dDataP: Paginated<IDeliverable> = await client.service(Services.Deliverable).find({query: {
        done:      false,
        processed: true,
        room_id:   this.getActiveMeeting.room_id,
        date:      {$lte: new Date().toISOString()},
        $limit:    0,
      }});

      console.log({
        qData,
        dData,
        qDataP,
        dDataP,
      });

      runInAction(() => {
        this.affected_items.total     = dData.total  + qData.total;
        this.affected_items.processed = dDataP.total + qDataP.total;
      });
    } catch (err) {
      console.error("Failed to count items affected by meeting", err);
    }
  }

  /**
   * Load initial attendance data.
   */
  protected load_attendanceData = async () => {
    if(!this.getActive) {
      runInAction(() => this.attendees = null);
      return;
    }

    runInAction(() => this.attendees = {});
    try {
      const logs: Paginated<ILog> = await this.log_service.find(paramsForServer({
        query: {
          $sort:      {"id": -1},
          event_type: {$in: ["enter", "leave"]},
          meeting_id: this.getActive.id,
        },
        noLinks: true,
      }));
      runInAction (() => 
      {
        for(const l of logs.data) {
          this.attendees[l.user_id] = this.attendees[l.user_id] || {checked_in: false};
          if(l.event_type === "enter" && new Date(l.created_at) >= (this.attendees[l.user_id].in || null)) 
            this.attendees[l.user_id].in  = new Date(l.created_at);
          if(l.event_type === "leave" && new Date(l.created_at) >= (this.attendees[l.user_id].out || null)) 
            this.attendees[l.user_id].out = new Date(l.created_at);
          
          this.attendees[l.user_id].checked_in = (this.attendees[l.user_id].in || null) > (this.attendees[l.user_id].out || null);
        }
      });
    } catch (err) {
      console.error("Failed to load meeting attendance", err);
      return;
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();
    // reaction(() => this.rootStore.spaceStore.curDate, () => this.loadRoom(), {name: "meeting_date_load"});
    
    when(() => {
      return !!(this.rootStore.spaceStore.getCurRoom)
    }, () => this.loadActiveMeeting());

    reaction(() => this.rootStore.spaceStore.getCurRoom && !!this.rootStore.spaceStore.getCurRoom.active_meeting_id, () => {
      if(!this.rootStore.spaceStore.getCurRoom?.active_meeting_id) runInAction(() => this.activeMeeting = null);
      else this.loadActiveMeeting();
    }, {name: "meeting_room_loadActive"});

  }
  protected getCurFilter = (): Query => this.query; 

  loadRoom = () => {
    super.loadRoom();

    this.query = {
      "meeting.room_id": this.rootStore.authStore.getCurRoomID,
      // "meeting.date": {
      //   $gte: add(startOfMonth(this.rootStore.spaceStore.curDate), {weeks: -1}).toISOString(),
      //   $lte: add(endOfMonth(this.rootStore.spaceStore.curDate),   {weeks:  1}).toISOString(),
      // }
    };
  }

  loadDashboard = () => {
    super.loadDashboard();
    this.storage.next.load();
    this.query = {
      "meeting.date": {
        $gte: add(startOfMonth(this.rootStore.spaceStore.curDate), {weeks: -1}).toISOString(),
        $lte: add(endOfMonth(this.rootStore.spaceStore.curDate),   {weeks:  1}).toISOString(),
      }
    };
  }

  clearDashboard = () => {
    this.storage.next.clear();
  }

  get getQueries(): {[name: string]: Query} {
    return {
      [MeetingStore.ContainerType.Next]: {
        "$sort":           { "meeting.date": 1},
        "$limit":          this.overviewLimit,
        "meeting.date":    { $gte: new Date().toISOString() }
      }, 
    }
  }

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  get getActiveMeeting(): Meeting {
    return this.activeMeeting;
  }

  get getActiveMeetingProgress(): number {
    return this.affected_items.total ? Math.floor((this.affected_items.processed / this.affected_items.total)*100) : 0;
  }

  get getCalendarEvents(): EventInput[] {
    if(this.rootStore.authStore.getCurRoomID)  {
      return this.getItems.map(m => {
        const start = new Date(m.start || m.date);
        let end   =  m.duration ? addMinutes(m.start, m.duration * 60) : undefined;
        
        if( end && end.getDay() != start.getDay()) {
          end = new Date(m.start || m.date);
          end.setMinutes(59);
        }

        return {
          id:        ""+m.id,
          number:    ""+m.number,
          groupId:   "meeting",
          type:      m.type,
          category:  m.category,
          title:     m.title,
          start:     start,
          end:       end,
          allDay:    !m.start,
          editable:  false,
          extendedProps: {
            active:    m.isActive,
          }
        }
      });
    } else return Object.entries(
        this.getItems
          .filter(i => !this.rootStore.spaceStore.projectFilter.length || 
            this.rootStore.spaceStore.projectFilterHasProject(i.getProjectID)
          )
          .reduce((res: {[date: string]: {count: number, items: Meeting[]}}, val) => {
            if(!val.date) return res;
            const key: Date = resetTime(val.date);
            res[key.toISOString()] = res[key.toISOString()] || {count: 0, items: []};
            res[key.toISOString()].count++;
            res[key.toISOString()].items.push(val);
            return res;
          }, {})).map(([key, value]) => ({
            id:        this.data_type,
            groupId:   this.data_type,
            title:     ""+value.count,
            color:     `${theme.color.items[(this.data_type as ItemType)]}`,
            start:     new Date(key),
            editable:  false,
            extendedProps: {
              items: value.items,
            }
          }));
  }

  /**
   * @return the total number of items tracked for progress during this meeting.
   */
  get getAffectedItems(): number {
    return this.affected_items.total;
  }
  /**
   * @return the number of items tracked for progress during this meeting with
   *        processed set to true.
   */
  get getProcessedItems(): number {
    return this.affected_items.processed;
  }

  /**
   * @return the users available in meeting room and not already linked
   */
  get getAvailableParticipants(): any {
    return this.rootStore.userStore.getUsersBySpace(this.getActive.room_id, [UserType.Default, UserType.Accountless]).map(u => ({value: u, label: u.displayName}));
  }

  /**
   * @return the users available in meeting room and is linked to the current item
   */
  get getParticipants(): User[] {
    return this.rootStore.userStore.getLinks;
  }

  isCheckedIn = async (user_id: number): Promise<boolean> => {
    try {
      const logs: Paginated<ILog> = await this.log_service.find(paramsForServer({
        query: {
          $select:    ["event_type"],
          $limit:     1,
          $sort:      {"id": -1},
          event_type: {$in: ["enter", "leave"]},
          meeting_id: this.activeMeeting.id,
          user_id,
        },
        noLinks: true,
      }));
      if(logs.total && logs.data[0].event_type === "enter") return true;
    } catch (err) {
      console.error("Failed to check attendance status of user", user_id, "for meeting", this.activeMeeting, err);
    }
    return false;
  }

  get getNextMeeting(): Meeting {
    const sorted = this.getItems.length ? this.getItems.filter( m => m.date > new Date()).sort((a,b) => a.date.getTime() < b.date.getTime() ? -1 : 0) : null;
    return sorted && sorted.length ? sorted[0] : null;
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  setActiveMeeting = (meeting: IMeeting) => {
    this.activeMeeting = meeting ? Meeting.create(meeting) : null;
  }

  startMeeting = async (meeting: Meeting) => {
    try {
      await meeting.startMeeting();
      this.setActiveMeeting(meeting);
      toast.success(i18next.t("Meeting started"));
    } catch (err) {
      console.error("Failed to start meeting", err);
      toast.error(i18next.t("failed_meeting_started"));
    }
  }

  stopMeeting = async (meeting: Meeting) => {
    try {
      await meeting.stopMeeting();
      this.setActiveMeeting(null);
      toast.success(i18next.t("meeting_stopped"));
    } catch (err) {
      console.error("Failed to stop meeting", err);
      toast.error(i18next.t("failed_meeting_stopped"));
    }
  }


  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  /**
    * Checks in an accountless user into a meeting. This is done by setting 
    * the current room_id as active room for that user. Log hooks will take 
    * care of the rest. 
    * 
    * Only the meeting leader can check in users. 
    * 
    * Normal users are checked in/out automatically. 
    * 
    * At the beginning/end of a meeting all users in that room are checked in/out automatically.
    * 
    * At the end of a meeting all accountless users are checked out by resetting their active 
    * room_id back to null. 
    */
  checkInOut = async (user_id: number): Promise<boolean> => {
    if(!this.activeMeeting || this.activeMeeting.leader_id !== this.rootStore.authStore.getCurUserID) return false;
    // if(this.rootStore.userStore.container[user_id].type !== UserType.Accountless) return false;

    const checkedIn = await this.isCheckedIn(user_id);
    
    try {
      if(checkedIn) {
        console.log('checkout out');
        await client.service(Services.Log).checkInOut({
          user_id:      user_id,
          workspace_id: this.activeMeeting.workspace_id,
          room_id:      Number(this.activeMeeting.room_id),
          item_id:      this.activeMeeting.id,
          data_type:    DataType.User,
          property:     null,
          data:         null,
          meeting_id:   this.activeMeeting.id,
          event_type:   'leave',
        });
        await this.isCheckedIn(user_id);
        runInAction(() => this.attendees[user_id].checked_in = false);
        
      }
      else  {
        console.log('check in user')
        await client.service(Services.Log).checkInOut({
          user_id:      user_id,
          workspace_id: this.activeMeeting.workspace_id,
          room_id:      Number(this.activeMeeting.room_id),
          item_id:      this.activeMeeting.id,
          data_type:    DataType.User,
          property:     null,
          data:         null,
          meeting_id:   this.activeMeeting.id,
          event_type:   'enter',
        });
        await this.isCheckedIn(user_id);
        runInAction(() => this.attendees[user_id]['checked_in'] = true);
      }
    } catch (err) {
      console.error("Failed to check in user", user_id, "into meeting", this.activeMeeting, err);
      return false;
    }

    return !checkedIn;
  }
}

export namespace MeetingStore {
  export enum ContainerType {
    Next = "next",
    Dashboard = "dashboard",
  }
}

